"use client";

import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useGameState } from "@/components/game-state-context";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import logger from "@/lib/logger";
import { cn } from "@/lib/utils";
import { callMcpTool } from "@/src/api/mcp";
import { shouldFireNudgeAfterRoll } from "./action-roll-card";
import { DEFAULT_MODIFIERS } from "./skill-check-modifiers";

type InitiativeRollCardProps = {
  actorId: string;
  initiative: number | null;
  campaignId: string;
  onResolved?: () => void;
};

export function InitiativeRollCard({
  actorId,
  initiative,
  campaignId,
  onResolved,
}: InitiativeRollCardProps) {
  const [rolling, setRolling] = useState(false);
  const [rollError, setRollError] = useState<string | null>(null);
  const [localResult, setLocalResult] = useState<number | null>(null);
  const { sheet } = useCharacterSheet(campaignId, actorId);
  const { invalidate } = useGameState();

  const handleRollInitiative = useCallback(async () => {
    setRolling(true);
    setRollError(null);
    try {
      const ack = await callMcpTool(
        "resolve_initiative_roll",
        {
          actorId,
          playerModifiers: DEFAULT_MODIFIERS,
        },
        campaignId
      );
      if (
        ack.data &&
        typeof ack.data === "object" &&
        "rollResult" in (ack.data as Record<string, unknown>)
      ) {
        const data = ack.data as Record<string, unknown>;
        const rollResult = data.rollResult as { total: number } | undefined;
        if (rollResult) {
          setLocalResult(rollResult.total);
        }
      }
      // Direct invalidate: fires immediately on ACK; TOOL_INVALIDATION_MAP
      // also invalidates via tool_complete SSE but can lag behind. Idempotent
      // — don't delete either path.
      invalidate(["encounter"]);
      if (shouldFireNudgeAfterRoll(ack)) {
        onResolved?.();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Initiative roll failed";
      logger.error({ err, actorId }, "Failed to resolve initiative roll");
      setRollError(message);
      // Invalidate even on error so we read authoritative state.
      invalidate(["encounter"]);
    } finally {
      setRolling(false);
    }
  }, [actorId, campaignId, invalidate, onResolved]);

  const displayTotal = localResult ?? initiative;

  if (displayTotal !== null) {
    // Resolved — large number + label
    return (
      <div className="my-2 rounded-lg border border-blue-500/50 bg-card p-4">
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          Initiative Roll
        </div>
        <div className="font-bold text-4xl text-card-foreground tabular-nums">
          {displayTotal}
        </div>
        <div className="mt-1 text-muted-foreground text-sm">
          {sheet?.name ?? actorId}
        </div>
      </div>
    );
  }

  // Pending — flat layout: header + initiative preview + Roll button
  return (
    <div className="my-2 rounded-lg border border-border bg-card p-4">
      <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
        Initiative Roll
      </div>
      <p className="mb-3 text-card-foreground text-sm">
        Roll d20 + Dex mod for <span>{sheet?.name ?? actorId}</span>
      </p>
      {/* The /sheet route Zod-validates the payload (initiativeBonus: z.number()),
          so this typeof guard is defense-in-depth against shape drift — the client
          fetcher does not re-validate. */}
      {sheet && typeof sheet.initiativeBonus === "number" && (
        <div className="mb-3 rounded border border-border/50 bg-muted/30 p-2 text-sm">
          <span className="text-muted-foreground">
            Initiative (already applied):{" "}
          </span>
          <span className="font-bold text-foreground">
            {sheet.initiativeBonus >= 0
              ? `+${sheet.initiativeBonus}`
              : sheet.initiativeBonus}
          </span>
        </div>
      )}
      {rollError && (
        <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
          {rollError}
        </div>
      )}
      <button
        className={cn(
          "rounded px-4 py-1.5 font-medium text-sm disabled:pointer-events-none disabled:opacity-50",
          "bg-blue-600 text-white hover:bg-blue-500"
        )}
        disabled={rolling}
        onClick={handleRollInitiative}
        type="button"
      >
        {rolling ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Rolling...
          </span>
        ) : (
          "Roll Initiative"
        )}
      </button>
    </div>
  );
}
